<script setup lang="ts">
import { computed } from '#imports';
import { UiSize, UiTheme } from '../../styles/Styles';
import { UiLoading } from '../loading';
import { type UiIconName, UiIconTemplate } from '../icon';
import type { QaID } from '../../utils/types';

const props = withDefaults(defineProps<{
  theme?: UiTheme | string;
  size?: UiSize;
  locale?: string;
  qa?: QaID;
  link?: boolean;
  disabled?: boolean;
  loading?: boolean;
  wrap?: boolean;
  iconLeft?: UiIconName;
  iconRight?: UiIconName;
  iconCenter?: UiIconName;
}>(), {
  theme: UiTheme.Primary,
  size: UiSize.Medium,
  locale: '',
  qa: '',
  link: false,
  disabled: false,
  loading: false,
  wrap: false,
})

const classView = computed(() => {
  const result = [];

  result.push(`_${props.theme}`);
  result.push(`_${props.size}`);
  if (props.disabled || props.loading) result.push('_disabled');
  if (props.link) result.push('_link');
  if (props.wrap) result.push('_wrap');

  return result;
});

defineSlots<{
  leftIcon?(): void;
  centerIcon?(): void;
  rightIcon?(): void;
}>();
</script>

<template lang="pug">
button.ui-button(
  type="button"
  v-bind="{ 'data-qa': qa + locale }"
  :disabled="disabled || loading"
  :class="[...classView, $slots.leftIcon ? '_left-icon' : '', $slots.rightIcon ? '_right-icon' : '', $slots.centerIcon ? '_center-icon' : '']"
)
  .button-icon.left-icon(
    v-if="$slots.leftIcon || iconLeft"
  )
    slot(name="leftIcon")
      UiIconTemplate(:name="iconLeft")
  .button-icon.center-icon(
    v-if="$slots.centerIcon || iconCenter"
  )
    slot(name="centerIcon")
      UiIconTemplate(:name="iconCenter")
  template(v-else)
    UiLoading.loading(:isShow="loading" :size="26")
    slot {{ $t(locale) }}
  .button-icon.right-icon(
    v-if="$slots.rightIcon || iconRight"
  )
    slot(name="rightIcon")
      UiIconTemplate(:name="iconRight")
</template>

<style lang="scss" scoped>
@import '../../styles/button';

.ui-button {
  @include ui-button-styles;
  position: relative;
}

.loading {
  position: absolute;
  left: calc(50% - 13px);
  top: calc(50% - 13px);
}
</style>
